import React, { useState } from 'react';
import { XMarkIcon, PlayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { triggerTestRun } from '../api/client';

function formatDateForInput(date) {
  return date.toISOString().split('T')[0];
}

export default function TestRunModal({ isOpen, rule, onClose }) {
  const navigate = useNavigate();

  // Default date range: last 7 days
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const [startDate, setStartDate] = useState(formatDateForInput(weekAgo));
  const [endDate, setEndDate] = useState(formatDateForInput(today));
  const [tenantId, setTenantId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !rule) return null;

  const validateDates = () => {
    if (!startDate || !endDate) return 'Start date and end date are required.';
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) return 'End date must be on or after start date.';
    const diffDays = (end - start) / (1000 * 60 * 60 * 24);
    if (diffDays > 30) return 'Date range cannot exceed 30 days.';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateDates();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const params = { startDate, endDate };
      if (tenantId.trim()) params.tenantId = tenantId.trim();

      const result = await triggerTestRun(rule.ruleId, params);
      onClose();
      navigate(`/admin/test-results/${encodeURIComponent(rule.ruleId)}/${encodeURIComponent(result.jobId)}`, {
        state: { ruleName: rule.ruleName, ruleType: rule.ruleType, parametersJson: rule.parametersJson, startDate, endDate, tenantId: tenantId.trim() || null, testRunId: result.testRunId },
      });
    } catch (err) {
      setError(err.message || 'Failed to start test run.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-white">Test Run</h3>
            <p className="text-sm text-gray-400 mt-0.5">{rule.ruleName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-300">
            Run this <span className="text-blue-400 font-medium">{rule.ruleType?.replace(/_/g, ' ')}</span> rule
            against historical data to see which events match.
          </p>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setError(null); }}
                className="input-field w-full text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setError(null); }}
                className="input-field w-full text-sm"
              />
            </div>
          </div>

          {/* Optional tenant filter */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Tenant ID <span className="text-gray-500">(optional — leave blank for all tenants)</span>
            </label>
            <input
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="e.g. 12345678-abcd-..."
              className="input-field w-full text-sm"
            />
          </div>

          {/* Info note */}
          <div className="text-xs text-gray-500 bg-gray-900/50 rounded p-2">
            Results are capped at 50 matches. Max date range: 30 days.
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-900/20 rounded p-2">
              <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-700">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={submitting}>
            Cancel
          </button>
          <button onClick={handleSubmit} className="btn-primary text-sm flex items-center gap-1.5" disabled={submitting}>
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Starting...
              </>
            ) : (
              <>
                <PlayIcon className="h-4 w-4" />
                Start Test Run
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
